import type { ReactNode } from "react"

import type { ApiProblem } from "../api/problem"
import { cx } from "../lib/cx"
import type { TenantRoleCode } from "@planerius/contracts/cabinet-auth"
import { Button } from "../ui/button"
import { deniedText } from "./denied-text"
import { Skeleton, SkeletonBlock, SkeletonCircle } from "../ui/skeleton"
import styles from "./states.module.css"
import "./shell-tokens.css"
import { useShellLayout } from "./use-viewport"

export { Skeleton }

/** Колонки каркаса сетки: ширины двух плашек в шапке мастера. Числа — из макета. */
const SKELETON_COLUMNS: readonly (readonly [number, number])[] = [
  [110, 60],
  [140, 70],
  [90, 50],
  [120, 80],
  [130, 60],
  [100, 70],
]

/** Заготовки записей: отступ сверху и высота. Пропуск по (i + j) % 3 — чтобы колонки
    не выстроились в одну линию: настоящий день так никогда не выглядит. */
const SKELETON_BLOCKS: readonly (readonly [number, number])[] = [
  [60, 46],
  [130, 78],
  [240, 46],
  [330, 104],
  [460, 60],
]

const SKELETON_HOURS: readonly string[] = [
  "",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
  "19:00",
  "20:00",
]

/** Карточки дня мастера: высота карточки и ширина первой плашки. Числа — из макета. */
const SKELETON_CARDS: readonly (readonly [number, number])[] = [
  [72, 150],
  [72, 120],
  [56, 90],
  [72, 160],
]

/**
 * Каркас всего кабинета. ОН ЖЕ — состояние «проверяем доступ».
 *
 * Это один компонент, а не два похожих, и это главное требование ТЗ к оболочке:
 * пока права неизвестны, экран обязан выглядеть как обычная загрузка. Человеку
 * с полными правами нельзя ни на долю секунды показать «нет доступа» — он поверит
 * и позвонит владельцу выяснять, за что его уволили.
 *
 * КАРКАС ПОВТОРЯЕТ ФОРМУ БУДУЩЕГО СОДЕРЖИМОГО, а не крутит спиннер: администратор
 * узнаёт свой журнал раньше, чем доехали данные, и не начинает искать его заново.
 * Поэтому у каркаса три вида — по числу раскладок: на телефоне журнал это список
 * карточек, а не сетка, и каркас обязан обещать именно список.
 *
 * Подпись «Проверяем доступ…» намеренно тихая и стоит там же, где потом будет панель
 * журнала. Она объясняет задержку, но не выглядит предупреждением: человека с доступом
 * нельзя заставить заподозрить, что его проверяют.
 */
export function AppLoading(): ReactNode {
  const layout = useShellLayout()

  if (layout === "phone") {
    return (
      <SkeletonBlock label="Проверяем доступ и загружаем кабинет" className={styles.phoneFrame}>
        <div className={styles.phoneHead}>
          <Skeleton width={120} height={17} radius={5} />
          <Skeleton width={180} height={11} radius={3} />
        </div>
        <div className={styles.checkingRow}>Проверяем доступ…</div>
        <div className={styles.phoneList}>
          {SKELETON_CARDS.map(([height, width], index) => (
            <div key={`${String(height)}-${String(index)}`} className={styles.phoneCard} style={{ height: `${height}px` }}>
              <Skeleton width={width} height={12} radius={4} />
              <Skeleton width={110} height={9} radius={3} />
            </div>
          ))}
        </div>
        <div className={styles.phoneTabbar}>
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className={styles.phoneTab}>
              <Skeleton width={46} height={9} radius={3} />
            </div>
          ))}
        </div>
      </SkeletonBlock>
    )
  }

  const icons = layout === "compact"

  return (
    <SkeletonBlock label="Проверяем доступ и загружаем кабинет" className={styles.frame}>
      <div className={icons ? styles.frameNavIcons : styles.frameNav} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((index) => (
          <div key={index} className={icons ? styles.navIconPlate : styles.navPlate} />
        ))}
      </div>

      <div className={styles.frameBody}>
        <div className={styles.frameTopbar}>
          <Skeleton width={96} height={14} />
          <Skeleton width={220} height={18} radius={6} />
        </div>

        <div className={styles.frameToolbar}>
          <Skeleton width={120} height={14} />
          <Skeleton width={180} height={14} />
          <span className={styles.checkingNote}>Проверяем доступ…</span>
        </div>

        <div className={styles.grid}>
          <div className={styles.gridScale}>
            {SKELETON_HOURS.map((hour, index) => (
              <div key={`${hour}-${String(index)}`} className={styles.gridHour}>
                {hour}
              </div>
            ))}
          </div>

          {SKELETON_COLUMNS.map(([wide, narrow], column) => (
            <div key={`${String(wide)}-${String(column)}`} className={styles.gridColumn}>
              <div className={styles.gridHead}>
                <SkeletonCircle size={26} />
                <div className={styles.gridHeadText}>
                  <Skeleton width={wide} height={10} radius={3} />
                  <Skeleton width={narrow} height={8} radius={3} />
                </div>
              </div>
              {SKELETON_BLOCKS.filter((_, row) => (column + row) % 3 !== 1).map(([top, height]) => (
                <div
                  key={`${String(top)}-${String(height)}`}
                  className={cx("plnr-skeleton", styles.gridBlock)}
                  style={{ top: `${top + 44}px`, height: `${height}px` }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </SkeletonBlock>
  )
}

/**
 * Сбой с подробностями. Отдельный экран, а не «нет доступа»: сессия цела, права просто
 * не доехали. Кнопка «Повторить» здесь главная — в половине случаев она всё и чинит.
 *
 * Код обращения показан машинописью и стоит отдельной строкой намеренно: его диктуют
 * в поддержку по телефону, и он обязан читаться посимвольно.
 */
export function ProblemScreen(props: {
  readonly problem: ApiProblem
  readonly onRetry?: (() => void) | undefined
}): ReactNode {
  const { problem } = props
  return (
    <div className={styles.centered}>
      <div className={styles.card} role="alert">
        <p className={styles.cardTitle}>{problem.title}</p>
        <p className={styles.cardText}>
          {problem.detail ?? "Попробуйте повторить запрос через минуту."}
        </p>
        {props.onRetry === undefined ? null : (
          <div className={styles.cardActions}>
            <Button size="sm" onClick={props.onRetry}>
              Повторить
            </Button>
          </div>
        )}
        <div className={styles.trace}>
          {problem.code} · {problem.status}
          {problem.instance === null ? "" : ` · ${problem.instance}`}
        </div>
      </div>
    </div>
  )
}

/**
 * Нет прав на раздел (1-Э1.11).
 *
 * Показывается ПО СВОЕМУ АДРЕСУ, а не перенаправлением на общий экран отказа: адрес
 * называет раздел, кнопка «назад» работает, и человек может переслать ссылку владельцу
 * со словами «сюда меня не пускает». Перенаправление всё это стирает.
 *
 * КОДА ПРАВА В ТЕКСТЕ НЕТ: «попросите открыть reports.view» — просьба, которую человек
 * не может произнести. Вместо кода — его роль и роли, у которых доступ есть (denied-text.ts).
 *
 * КНОПКА ЕСТЬ, И ОНА ВЕДЁТ В ЖУРНАЛ. Тупик без выхода человек закрывает вкладкой,
 * а журнал открыт всем, у кого вообще есть кабинет.
 */
export function DeniedScreen(props: {
  readonly sectionLabel: string
  readonly required: string
  /** Роль зрителя: код системной роли и её название в этом салоне. */
  readonly viewerRole?: TenantRoleCode | null
  readonly viewerRoleName?: string
  readonly onHome?: (() => void) | undefined
  /** Куда ведёт кнопка: «В журнал», «В отчёты» — первый открытый человеку раздел. */
  readonly homeLabel?: string
}): ReactNode {
  const text = deniedText({
    sectionLabel: props.sectionLabel,
    permission: props.required,
    viewerRole: props.viewerRole ?? null,
    viewerRoleName: props.viewerRoleName ?? "своя роль салона",
  })
  return (
    <div className={styles.centered}>
      <div className={styles.card}>
        <p className={styles.cardTitle}>{text.title}</p>
        <p className={styles.cardText}>{text.text}</p>
        {props.onHome === undefined ? null : (
          <div className={styles.cardActions}>
            <Button size="sm" onClick={props.onHome}>
              В {props.homeLabel === undefined ? "журнал" : props.homeLabel.toLowerCase()}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
